<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { usage } from "../helpers/constants";
  import type { InvoiceLine, Recipe, RecipeFull, RecipeLineMatch } from "../helpers/types";

  export let recipe: RecipeFull;
  export let connectedLinesManual: RecipeLineMatch[];
  export let ingredientSoldQty: Record<string, number>;
  export let recipeSold: Record<string, number>;
  export let totalSalesDays: number;
  export let allInvoiceLines: InvoiceLine[];
  export let indent = 0;
  export let ingredientPriceOverrides: Record<string, number | null> = {};

  let totalPrice: number;
  let itemsExpanded: Record<string, boolean> = {};
  let indentSpace = 10;

  const dispatch = createEventDispatcher();

  const totalRecipePrice = (recipe: Recipe) => {
    let totalPrice = 0;

    if (recipe.items) {
      for (const ingredient of recipe.items) {
        if (ingredient.type === "recipe") {
          totalPrice += totalRecipePrice(ingredient as Recipe); // assuming type 'recipe' means it's a Recipe
        } else {
          const overridePrice = ingredientPriceOverrides[recipe.id + "-" + ingredient.id];
          const ingredientPrice = ingredient.price_per_day ?? 0; // Use 0 if price_per_day is undefined
          totalPrice += overridePrice ?? ingredientPrice;
        }
      }
    }

    return totalPrice;
  };

  function getOverrideValue(recipeId: string, ingredientId: string): string {
    const key = recipeId + "-" + ingredientId;
    const override = ingredientPriceOverrides[key];
    if (override == null) {
      return "";
    }
    const value = (override * totalSalesDays) / recipeSold[recipeId] / 100;
    return value > 0 ? value.toFixed(2) : "";
  }

  $: isNotNull = (ingredientId: string): boolean => {
    const override = ingredientPriceOverrides[ingredientId];
    return override !== null && override !== undefined && override >= 0;
  };

  $: {
    if (JSON.stringify(ingredientPriceOverrides) != "") {
      totalPrice = totalRecipePrice(recipe);
    }
  }
</script>

{#if totalPrice}
  <tr class={indent > 0 ? "ingredient-row" : "recipe-row"}>
    <td style="padding-left: {indent * indentSpace}px;">
      {recipe.name}
      {#if recipe.usage}<small>{usage[recipe.usage]}</small>{/if}
    </td>
    <td class="text-ocd" style="text-align: right;">
      {((totalPrice * totalSalesDays) / recipeSold[recipe.id] / 100).toFixed(2)}
    </td>
    <!-- <td class="text-ocd" style="text-align: right;">
      {((totalPrice * totalSalesDays) / 100).toFixed(2)}
    </td> -->
  </tr>
{/if}
{#each recipe.items as ingredientData (ingredientData.id + "-" + (ingredientPriceOverrides[recipe.id + "-" + ingredientData.id] || 0))}
  {#if ingredientData.type == "recipe"}
    <svelte:self
      recipe={ingredientData}
      {connectedLinesManual}
      {ingredientSoldQty}
      {recipeSold}
      {totalSalesDays}
      {allInvoiceLines}
      indent={indent + 1}
      {ingredientPriceOverrides}
      on:ingredientPriceOverride={(e) => {
        dispatch("ingredientPriceOverride", e.detail);
      }}
    />
  {:else}
    <tr class="ingredient-row">
      <td style="padding-left: {indentSpace + indent * indentSpace}px;">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="expandable"
          on:click={() =>
            (itemsExpanded[ingredientData.id] = itemsExpanded[ingredientData.id] ? false : true)}
        >
          {#if !ingredientData.no_attached_invoice_lines}
            <div class="expand-icon">
              {itemsExpanded[ingredientData.id] ? "-" : "+"}
            </div>
          {/if}
          {ingredientData.name} <small>{usage[ingredientData.usage]}</small>
        </div>
      </td>
      <td class="text-ocd" style="text-align: right;">
        {#if isNotNull(`${recipe.id}-${ingredientData.id}`)}
          <input
            autofocus={true}
            type="number"
            class="ingredient-price-input text-ocd"
            size={7}
            min={0}
            placeholder={ingredientData.no_attached_invoice_lines
              ? `0.00`
              : (
                  (ingredientData.price_per_day * totalSalesDays) /
                  recipeSold[recipe.id] /
                  100
                ).toFixed(2)}
            value={getOverrideValue(recipe.id, ingredientData.id)}
            on:change={(e) => {
              e.preventDefault();
              e.stopImmediatePropagation();

              if (e.target instanceof HTMLInputElement) {
                dispatch("ingredientPriceOverride", {
                  ingredientId: recipe.id + "-" + ingredientData.id,
                  price: e.target.value
                    ? ((Number(e.target.value) * 100) / totalSalesDays) * recipeSold[recipe.id]
                    : null,
                });
                e.target.blur();
              }
            }}
            on:keydown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopImmediatePropagation();
                if (e.target instanceof HTMLInputElement) {
                  dispatch("ingredientPriceOverride", {
                    ingredientId: recipe.id + "-" + ingredientData.id,
                    price: null,
                  });
                  e.target.value = "";
                  e.target.blur();
                }
              }
            }}
          />
        {:else}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            class="overridable"
            on:click={() =>
              dispatch("ingredientPriceOverride", {
                ingredientId: recipe.id + "-" + ingredientData.id,
                price: ingredientData.no_attached_invoice_lines ? 0 : ingredientData.price_per_day,
              })}
          >
            <div class="edit">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                  id="SVGRepo_tracerCarrier"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /><g id="SVGRepo_iconCarrier"
                  ><path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="m3.99 16.854-1.314 3.504a.75.75 0 0 0 .966.965l3.503-1.314a3 3 0 0 0 1.068-.687L18.36 9.175s-.354-1.061-1.414-2.122c-1.06-1.06-2.122-1.414-2.122-1.414L4.677 15.786a3 3 0 0 0-.687 1.068zm12.249-12.63 1.383-1.383c.248-.248.579-.406.925-.348.487.08 1.232.322 1.934 1.025.703.703.945 1.447 1.025 1.934.058.346-.1.677-.348.925L19.774 7.76s-.353-1.06-1.414-2.12c-1.06-1.062-2.121-1.415-2.121-1.415z"
                    fill="#000000"
                  /></g
                ></svg
              >
            </div>
            {ingredientData.no_attached_invoice_lines
              ? `—`
              : (
                  (ingredientData.price_per_day * totalSalesDays) /
                  recipeSold[recipe.id] /
                  100
                ).toFixed(2)}
          </div>
        {/if}
      </td>
      <!-- <td class="text-ocd" style="text-align: right;">
        {ingredientData.no_attached_invoice_lines
          ? `—`
          : ((ingredientData.price_per_day * totalSalesDays) / 100).toFixed(2)}
      </td> -->
    </tr>
    {#each connectedLinesManual as ingredient}
      {#if ingredient.recipe_ingredient_item_id == ingredientData.id}
        {#each ingredient.invoice_lines as invoiceLine}
          <tr
            class="invoice-line-row"
            class:expanded={itemsExpanded[ingredientData.id]}
            class:overriden={ingredientPriceOverrides[recipe.id + "-" + ingredientData.id] &&
              ingredientPriceOverrides[recipe.id + "-" + ingredientData.id] !=
                ingredientData.price_per_day}
          >
            <td style="padding-left: {2 * indentSpace + indent * indentSpace}px;">
              {invoiceLine.invoice_line_item_name}
            </td>
            <td class="text-ocd" style="text-align: right;">
              {(
                (totalSalesDays *
                  (((allInvoiceLines.find(
                    (line) => line.invoice_line_item_id === invoiceLine.invoice_line_item_id
                  )?.total_price_cents ||
                    allInvoiceLines.find(
                      (line) => line.invoice_line_item_id === invoiceLine.invoice_line_item_id
                    )?.total_price_cents ||
                    0) /
                    ingredientData.days_spent_for) *
                    (ingredientData.quantity / ingredientSoldQty[ingredientData.id]))) /
                100
              ).toFixed(2)}
            </td>
            <!-- <td class="text-ocd" style="text-align: right;">
              {(
                (((totalSalesDays *
                  (allInvoiceLines.find(
                    (line) => line.invoice_line_item_id === invoiceLine.invoice_line_item_id
                  )?.total_price_cents ||
                    allInvoiceLines.find(
                      (line) => line.invoice_line_item_id === invoiceLine.invoice_line_item_id
                    )?.total_price_cents ||
                    0)) /
                  ingredientData.days_spent_for) *
                  (ingredientData.quantity / ingredientSoldQty[ingredientData.id])) /
                100
              ).toFixed(2)}
            </td> -->
          </tr>
        {/each}
      {/if}
    {/each}
  {/if}
{/each}

<style>
  small {
    display: inline-block;
    font-size: 50%;
    border-radius: 2px;
    padding: 2px;
    background-color: rgba(0, 0, 0, 0.2);
    line-height: 1;
  }
  .recipe-row {
    font-size: 120%;
  }
  .recipe-row td {
    padding-top: 10px;
  }
  .ingredient-row {
    opacity: 0.6;
  }
  .invoice-line-row {
    display: none;
    opacity: 0.3;
  }
  .invoice-line-row.expanded {
    display: table-row;
  }
  .overriden {
    text-decoration: line-through;
  }
  .ingredient-price-input {
    font-size: inherit;
    padding: unset;
    background-color: #fff;
    box-shadow: 0px 0px 2px inset #0003;
    border-radius: 3px;
    text-align: right;
    max-width: 80px;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }

  .overridable {
    cursor: pointer;
    position: relative;
    width: min-content;
    margin-left: auto;
  }

  .overridable:hover .edit {
    opacity: 1;
  }

  .overridable .edit {
    display: block;
    position: absolute;
    left: 100%;
    margin-left: 5px;
    width: 16px;
    height: 16px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.2;
  }
  .expand-icon {
    display: inline-block;
    width: 10px;
    text-align: center;
  }
</style>
